import React, { useState, useEffect, useContext } from "react";
import {
  Breadcrumb,
  Button,
  Table,
  Modal,
} from "flowbite-react";
import type { FC } from "react";
import {
  HiHome,
  HiCheck,
  HiX,
  HiTrash,
  HiArrowLeft,
} from "react-icons/hi";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import { useNavigate } from "react-router-dom";
import { createRequestControlMessage } from "../utils/cmsgBuilder";
import { ChatContext, ChatContextInterface } from "../components/MSRContext";
import { MyRequest } from "@/models/MyRequest";
import { useTranslation } from "react-i18next";

const INVITE_MEMBER_EXPIRY_MS = 5 * 60 * 1000;

const isInviteMemberExpired = (request: MyRequest): boolean => {
  if (request.type !== 'invite-member' || request.status !== 'pending') return false;
  return Date.now() - new Date(request.createdTime).getTime() > INVITE_MEMBER_EXPIRY_MS;
};

const RequestList: FC = function () {


  const [requests, setRequests] = useState<MyRequest[]>([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const { wrapper, requestController } = useContext(ChatContext) as ChatContextInterface;
  const navigate = useNavigate();
  const { t } = useTranslation("request");
  const { t: tCommon } = useTranslation("common");

  const loadRequests = () => {
    const savedRequests = localStorage.getItem("requests");
    if (savedRequests) {
      const parsedRequests: MyRequest[] = JSON.parse(savedRequests);
    } else {
      const fetchedRequests = wrapper?.requestcontroller?.Requests || [];
      setRequests(fetchedRequests);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [wrapper]);

  useEffect(() => {
    const updateRequests = () => {
      setRequests([...requestController.Requests]); // Ensures the state updates when new requests arrive
    };

    updateRequests(); // Initial load

    const interval = setInterval(updateRequests, 2000); // Polling every 2 seconds (can be replaced with event listeners if available)

    return () => clearInterval(interval);
  }, [requestController]);


  const saveRequestsToLocalStorage = (updatedRequests: MyRequest[]) => {
    localStorage.setItem("requests", JSON.stringify(updatedRequests));
  };

  const handleAccept = (request: MyRequest) => {
    requestController.acceptRequest(request);
    const updatedRequests = [...requestController.Requests];
    setRequests(updatedRequests);
    saveRequestsToLocalStorage(updatedRequests);
  };

  const handleReject = (request: MyRequest) => {

      // remove this condtion after scont support for rejecting invite-memeber request is added 
    if (request.type === "invite-member") {
      return;
    }
    requestController.rejectRequest(request);
    const updatedRequests = [...requestController.Requests];
    setRequests(updatedRequests);
    saveRequestsToLocalStorage(updatedRequests);
  };

  const handleCancelRequest = (request: MyRequest) => {
    wrapper?.sendMsg(
      createRequestControlMessage(
        request.id,
        "cancel-ongoing",
        `job-to-cancel:${request.type}, response-required:YES`
      ),
      "application/rechat.cntl+json"
    );
    requestController.updateRequestStatusById(request.id, "cancelled");
    const updatedRequests = requests.map((req) =>
      req.id === request.id ? { ...req, status: "cancelled" } : req
    );

    setRequests(updatedRequests);
    saveRequestsToLocalStorage(updatedRequests);
  };

  const filteredRequests = requests.filter(request => {
    if (activeFilter === "incoming") return request.incoming;
    if (activeFilter === "outgoing") return !request.incoming;
    return true;
  });

  return (
    <NavbarSidebarLayout>
      <div className="items-center justify-between block p-3 sm:p-4 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-800">
        <div className="w-full mb-1">
          <div className="mb-4">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="#">
                <div className="flex items-center gap-x-2 sm:gap-x-3">
                  <HiHome className="text-lg sm:text-xl" />
                  <span className="dark:text-white text-sm sm:text-base">{tCommon("home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item className="text-sm sm:text-base">{t("requests")}</Breadcrumb.Item>
            </Breadcrumb>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white sm:text-xl lg:text-2xl">
              {t("yourRequests")}
            </h1>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        {/* Mobile Card View */}
        <div className="block sm:hidden p-3 max-h-96 overflow-y-auto">
          {filteredRequests.length > 0 ? (
            <div className="space-y-2">
              {filteredRequests.map((request) => (
                <div key={request.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          {request.type}
                        </span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {request.incoming ? request.sender.split("@")[0] : request.receiver[0].split("@")[0]}
                        </span>
                        <div className="flex items-center mt-1">
                          {(() => { const ds = isInviteMemberExpired(request) ? 'expired' : request.status; return (<>
                          <div className={`mr-1 h-2 w-2 rounded-full ${ds === 'pending' ? 'bg-yellow-400' :
                            ds === 'accepted' ? 'bg-green-400' :
                            ds === 'rejected' ? 'bg-red-400' :
                            ds === 'expired' ? 'bg-orange-400' :
                            'bg-gray-400'
                            }`}></div>
                          <span className={`text-xs capitalize ${ds === 'expired' ? 'text-orange-500 dark:text-orange-400' : 'text-gray-600 dark:text-gray-400'}`}>
                            {ds}
                          </span>
                          </>); })()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {request.incoming ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {t("direction.in")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                          {t("direction.out")}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex space-x-1">
                    {request.incoming && request.status === 'pending' && !isInviteMemberExpired(request) && (
                      <>
                        <Button
                          size="sm"
                          color="success"
                          onClick={() => handleAccept(request)}
                          className="flex-1 text-xs px-2 py-1"
                        >
                          <HiCheck className="w-3 h-3 mr-1" />
                           {tCommon("accept")}
                        </Button>
                        <Button
                          size="sm"
                          color="failure"
                          disabled={request.type === "invite-member"}
                          onClick={() => handleReject(request)}
                          className="flex-1 text-xs px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <HiX className="w-3 h-3 mr-1" />
                          {tCommon("reject")}
                        </Button>
                      </>
                    )}
                    {!request.incoming && request.status === 'pending' && (
                      <Button
                        size="sm"
                        color="failure"
                        onClick={() => handleCancelRequest(request)}
                        className="flex-1 text-xs px-2 py-1"
                      >
                        <HiTrash className="w-3 h-3 mr-1" />
                        {tCommon("cancel")}
                      </Button>
                    )}
                    {request.status !== 'pending' && (
                      <div className="flex-1 text-center py-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {t("noActionsAvailable")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">{t("noRequestsFound")}</p>
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden sm:block p-4">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden shadow rounded-lg">
                <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                  <Table.Head className="bg-gray-100 dark:bg-gray-700">
                    <Table.HeadCell>{t("type.title")}</Table.HeadCell>
                    <Table.HeadCell>{t("type.senderOrReceiver")}</Table.HeadCell>
                    <Table.HeadCell>{t("type.direction")}</Table.HeadCell>
                    <Table.HeadCell>{t("type.status")}</Table.HeadCell>
                    <Table.HeadCell>{t("type.actions")}</Table.HeadCell>
                  </Table.Head>
                  <Table.Body className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-800">
                    {filteredRequests.length > 0 ? (
                      filteredRequests.map((request) => (
                        <Table.Row key={request.id} className="hover:bg-gray-100 dark:hover:bg-gray-700">
                          <Table.Cell className="p-4 text-base font-medium text-gray-900 whitespace-nowrap dark:text-white">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                              {request.type}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="p-4 text-base font-medium text-gray-900 whitespace-nowrap dark:text-white">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full flex items-center justify-center text-white text-sm font-semibold mr-3">
                                {(request.incoming ? request.sender.split("@")[0] : request.receiver[0].split("@")[0]).charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                {request.incoming ? 'From' : 'To'}   {request.incoming
                                    ? request.sender.split("@")[0]
                                    : Array.isArray(request.receiver)
                                        ? (request.receiver[0]?.includes(":")
                                          ? request.receiver[0].split(":")[1]
                                          : request.receiver[0]?.split("@")[0])
                                        : (request.receiver.includes(":")
                                          ? request.receiver.split(":")[1]
                                          : request.receiver.split("@")[0])
                                  }
                                </div>
                              
                              </div>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="p-4 text-base font-medium text-gray-900 whitespace-nowrap dark:text-white">
                            {request.incoming ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                                </svg>
                                {t("direction.incoming")}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                                </svg>
                                {t("direction.outgoing")}
                              </span>
                            )}
                          </Table.Cell>
                          <Table.Cell className="p-4 text-base font-medium text-gray-900 whitespace-nowrap dark:text-white">
                            {(() => { const ds = isInviteMemberExpired(request) ? 'expired' : request.status; return (
                            <div className="flex items-center">
                              <div className={`mr-2 h-2.5 w-2.5 rounded-full ${ds === 'pending' ? 'bg-yellow-400' :
                                ds === 'accepted' ? 'bg-green-400' :
                                ds === 'rejected' ? 'bg-red-400' :
                                ds === 'expired' ? 'bg-orange-400' :
                                'bg-gray-400'
                                }`}></div>
                              <span className={`capitalize ${ds === 'expired' ? 'text-orange-500 dark:text-orange-400' : ''}`}>{ds}</span>
                            </div>
                            ); })()}
                          </Table.Cell>
                          <Table.Cell className="p-4 whitespace-nowrap">
                            <div className="flex items-center gap-x-2">
                              {request.incoming && request.status === 'pending' && !isInviteMemberExpired(request) && (
                                <>
                                  <Button
                                    size="sm"
                                    color="success"
                                    onClick={() => handleAccept(request)}
                                    className="text-xs"
                                  >
                                    <HiCheck className="w-4 h-4 mr-1" />
                                    {tCommon("accept")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    color="failure"
                                    disabled={request.type === "invite-member"}
                                    onClick={() => handleReject(request)}
                                    className="text-xs disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    <HiX className="w-4 h-4 mr-1" />
                                    {tCommon("reject")}
                                  </Button>
                                </>
                              )}
                              {!request.incoming && request.status === 'pending' && (
                                <Button
                                  size="sm"
                                  color="failure"
                                  onClick={() => handleCancelRequest(request)}
                                  className="text-xs"
                                >
                                  <HiTrash className="w-4 h-4 mr-1" />
                                  {tCommon("cancel")}
                                </Button>
                              )}
                              {request.status !== 'pending' && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {tCommon("noActionsAvailable")}
                                </span>
                              )}
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ))
                    ) : (
                      <Table.Row>
                        <Table.Cell colSpan={5} className="p-8 text-center text-gray-500">
                          <div className="flex flex-col items-center justify-center">
                            <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h2m0 0V6a2 2 0 012-2h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V9M8 13h2m-2 4h.01" />
                            </svg>
                            <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">{t("noRequestsFound")}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400"> {t("noAvailableRequests")}</p>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

export default RequestList;